/**
 * CustomerMessages (COMMS-02) — the customer's ONE append-only thread: every
 * text, email, chat and logged call in time order, regardless of rail
 * (the Front/HubSpot timeline model; ServiceTitan keeps the same audit trail
 * per job). Provider-agnostic by design: the rows here come from the Podium
 * webhooks and M365 sends when COMMS-03 lands — and from manual logging
 * TODAY. Logging a call is real CRM machinery (every competitor has it),
 * not a stopgap: the engine writes to the same table.
 *
 * Sending does NOT live here yet — no fake composer. The send button arrives
 * with the engine and its who-gets-this preview (CONTACT-03 + consent).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Phone,
  Mail,
  MessageSquare,
  MessageCircle,
  Globe,
  StickyNote,
  Plus,
  FileText,
  CalendarPlus,
  Sparkles,
  Loader2,
  Trash2,
  Undo2,
  Lock,
  type LucideIcon,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useCustomerConsent, consentReason, type ConsentPurpose } from "@/features/comms/consent";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";
import { currentBurst, buildTranscript, burstScopeLabel, hasInbound } from "@/lib/conversationBurst";
import { mapUrgency, mapWindow, looksLikeWebForm, type BuilderUrgency } from "@/lib/intakeMap";
import { useSetMessageDeleted } from "@/features/inbox/api";
import { useCustomerMergeContext, applyMergeFields } from "@/lib/mergeFields";
import { useContacts, type ContactRow } from "./CustomerContacts";
import { CallRecordingPlayer } from "@/features/calls/CallRecordingPlayer";
import { useCommsPref } from "@/features/comms/comms";
import { CreateDispatchModal } from "./CreateDispatchModal";
import { onErrToast } from "@/components/ui/mutationToast";
import { InfoTip } from "@/components/InfoTip";

export interface MessageRow {
  id: string;
  custno: string | null;
  locno: string | null;
  channel: "sms" | "email" | "chat" | "web" | "call" | "note";
  direction: "inbound" | "outbound";
  message_type: string | null;
  endpoint: string | null;
  subject: string | null;
  body: string;
  status: string;
  provider: string;
  pegasus_dispatch_id: string | null;
  esc_dispatch: string | null;
  logged_by: string | null;
  /** CALL-REC-1: the call_event behind a channel='call' row — drives the
   *  inline recording player when the call was recorded. */
  call_event_id: string | null;
  /** Attachment manifest (name/type/size + provider id) — content stays in
   * the mailbox, fetched on demand (extract-attachment). */
  attachments:
    | { id: string; name: string; contentType: string; size: number }[]
    | null;
  created_at: string;
}

const CHANNEL_META: Record<
  MessageRow["channel"],
  { icon: LucideIcon; label: string }
> = {
  call: { icon: Phone, label: "Call" },
  sms: { icon: MessageSquare, label: "Text" },
  email: { icon: Mail, label: "Email" },
  chat: { icon: MessageCircle, label: "Chat" },
  web: { icon: Globe, label: "Web" },
  note: { icon: StickyNote, label: "Note" },
};

export function useMessages(custno: string) {
  return useQuery({
    queryKey: ["messages", custno],
    staleTime: 15 * 1000,
    queryFn: async (): Promise<MessageRow[]> => {
      const { data, error } = await supabase
        .from("message")
        .select("*")
        .eq("custno", custno)
        .is("deleted_at", null) // soft-deleted messages are hidden (Seth 2026-06-26)
        // Newest first (Seth 2026-06-13): the thread reads top→bottom = most
        // recent → oldest, and limit(300) now keeps the NEWEST 300, not the oldest.
        .order("created_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      return (data ?? []) as MessageRow[];
    },
  });
}

function useLogMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<MessageRow> & { custno: string; body: string }) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase.from("message").insert({
        ...v,
        status: "logged",
        provider: "manual",
        logged_by: userData?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["messages"] }),
  });
}

// ---------------------------------------------------------------------------
// Reply templates (TEMPLATES-01) — Pegasus-owned saved replies (seeded once from
// Podium, then edited in Settings). The composer's "Insert template" picker drops
// the body (and subject for email) into the draft, filling Podium-style merge
// fields like :contact_first_name: with the customer's name.
// ---------------------------------------------------------------------------
interface ReplyTemplate {
  id: string;
  title: string;
  body: string;
  subject: string | null;
  variables: string[];
}

function useReplyTemplates() {
  return useQuery({
    queryKey: ["reply_templates"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ReplyTemplate[]> => {
      const { data, error } = await supabase
        .from("reply_template")
        .select("id, title, body, subject, variables")
        .eq("archived", false)
        .order("sort");
      if (error) throw error;
      return (data ?? []) as ReplyTemplate[];
    },
  });
}

export function TemplatePicker({
  custno,
  onPick,
  dropUp,
}: {
  custno: string;
  onPick: (body: string, subject: string | null) => void;
  /** Open the list upward (for a composer docked at the bottom of the screen). */
  dropUp?: boolean;
}) {
  const { data: templates } = useReplyTemplates();
  const { data: ctx } = useCustomerMergeContext(custno);
  const [open, setOpen] = useState(false);
  // TPL-CLIP-01 (Seth 2026-10-20: "insert template … is cut off" in the Text /
  // Email windows): the list is PORTALED to the body and placed from the
  // button's rect, so a modal's scroll box can't clip it. It opens downward
  // when there is room for it, upward otherwise (or when asked to).
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number } | null>(null);
  const toggle = () => {
    if (open) { setOpen(false); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const PANEL_H = 288; // max-h-72
    const below = window.innerHeight - r.bottom;
    const up = dropUp || (below < PANEL_H + 12 && r.top > below);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - 288 - 8));
    setPos(up ? { left, bottom: window.innerHeight - r.top + 4 } : { left, top: r.bottom + 4 });
    setOpen(true);
  };
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("resize", close);
    return () => window.removeEventListener("resize", close);
  }, [open]);
  const list = templates ?? [];
  if (list.length === 0) return null;
  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
      >
        <FileText className="h-3 w-3" /> Insert template
      </button>
      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[200]" onClick={() => setOpen(false)} />
          <div
            style={{ position: "fixed", left: pos.left, top: pos.top, bottom: pos.bottom, zIndex: 201 }}
            className={cn("max-h-72 w-72 overflow-y-auto rounded-lg border border-hairline bg-white py-1 shadow-e2")}
          >
            {list.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  onPick(applyMergeFields(t.body, ctx), t.subject ? applyMergeFields(t.subject, ctx) : null);
                  setOpen(false);
                }}
                className="block w-full px-3 py-1.5 text-left transition-colors hover:bg-slate-50"
              >
                <span className="block text-xs font-medium text-slate-800">{t.title}</span>
                <span className="block truncate text-[11px] text-slate-400">
                  {t.body.replace(/\s+/g, " ").slice(0, 52)}
                </span>
              </button>
            ))}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function CustomerMessages({ custno, locno, multiProperty }: {
  custno: string;
  /** MSG-SCOPE (Seth 2026-08-21, Verde): on multi-property accounts, another
   *  tenant's texts must not read as THIS property's thread. */
  locno?: string | null;
  multiProperty?: boolean;
}) {
  const { data: allMessages, isLoading } = useMessages(custno);
  // Tolerant scope, same rule as notes/signals: this property's messages +
  // messages saved with no property. The rest stay one click away — hidden by
  // default, never gone (a property manager sometimes wants the whole account).
  const [showAllProps, setShowAllProps] = useState(false);
  const scoped = !!multiProperty && !!locno && !showAllProps;
  const messages = scoped
    ? (allMessages ?? []).filter((m) => !m.locno || m.locno === locno)
    : allMessages;
  const otherCount = (allMessages ?? []).length - (messages ?? []).length;
  const canLog = useCan("comms.log");
  const canSend = useCan("comms.send");
  // COMMS-UX-01: when a channel is OFF at the account level, the compose
  // button locks with the reason — the server gate would suppress the send
  // anyway; this makes the truth visible BEFORE someone types a message.
  // Unlocking = flipping the preference on the Reach card (deliberate, audited).
  const commsPref = useCommsPref(custno).data;
  const [logging, setLogging] = useState(false);
  const [composing, setComposing] = useState(false);
  const [texting, setTexting] = useState(false);
  // RECORDING→DISPATCH: holds the call_event_id when "Make a dispatch" is clicked
  // on a call bubble; the new dispatch carries source_call_id so the recording
  // attaches to it (Seth 2026-06-25).
  // pendingDispatch opens the create modal — from a call (callId → recording
  // attaches) OR from a chat / web-form bubble (initialDescription = the AI
  // summary, prefilled + editable). Seth 2026-06-25.
  const [pendingDispatch, setPendingDispatch] = useState<{
    callId?: string;
    initialDescription?: string;
    sourceCaption?: string;
    prefillUrgency?: BuilderUrgency | null;
    prefillWindow?: string | null;
    prefillTypeName?: string | null;
    /** 360-AUDIT wave 2 (H): the conversation's property, when the message knows it. */
    locnoHint?: string | null;
  } | null>(null);
  // 360-AUDIT wave 2 (D): booking from a conversation used to hard-prefill
  // property #1. Multi-property accounts now pick the property first (the
  // message's own locno skips the question when it's on file).
  const [dispatchLocno, setDispatchLocno] = useState<string | null>(null);
  const [summarizingId, setSummarizingId] = useState<string | null>(null);
  const { data: prefill } = useQuery({
    queryKey: ["dispatch_prefill", custno],
    enabled: !!pendingDispatch,
    queryFn: async () => {
      const [c, l] = await Promise.all([
        supabase.from("mirror_customer").select("fullname").eq("custno", custno).maybeSingle(),
        supabase.from("mirror_location").select("locno, add1, city, phone1").eq("custno", custno).order("locno").limit(10),
      ]);
      const locations = ((l.data ?? []) as { locno: string; add1: string | null; city: string | null; phone1: string | null }[]);
      return {
        name: (((c.data?.fullname as string | undefined) ?? "").replace(/\s+\d{4,}$/, "")) || "Customer",
        locations: locations.length > 0 ? locations : [{ locno: "00001", add1: null, city: null, phone1: null }],
      };
    },
  });
  // AI-INTAKE-SUMMARY: summarize a Podium chat / web-form bubble's text, then
  // open the create modal with the summary prefilled (propose-style — it's an
  // editable notes field, nothing books until the dispatcher saves). Falls back
  // to the raw text if the AI hiccups.
  // Summarize the WHOLE current back-and-forth (not one message — Seth
  // 2026-06-26: grabbing a single bubble drops the rest of the conversation).
  // The burst = the recent contiguous run of messages, both sides, gap-scoped.
  const burst = useMemo(() => currentBurst(messages ?? []), [messages]);
  const canSummarize = hasInbound(burst);
  // Shared intake: send text → summarize-intake → open the builder prefilled
  // with the summary (notes) AND the structured fields (urgency, window) mapped
  // onto the actual builder controls (Seth 2026-06-26 — "work as much info in").
  const runIntake = async (text: string, caption: string, id: string, locnoHint?: string | null) => {
    setSummarizingId(id);
    try {
      const { data } = await supabase.functions.invoke("summarize-intake", { body: { text } });
      const d = data as { ok?: boolean; summary?: string; fields?: { urgency?: string; promised_window?: string; suggested_service_type?: string } } | null;
      setPendingDispatch({
        initialDescription: d?.ok && d.summary ? d.summary : text,
        sourceCaption: caption,
        prefillUrgency: mapUrgency(d?.fields?.urgency),
        prefillWindow: mapWindow(d?.fields?.promised_window),
        prefillTypeName: d?.fields?.suggested_service_type || null,
        locnoHint: locnoHint ?? null,
      });
    } catch {
      setPendingDispatch({ initialDescription: text, sourceCaption: caption, locnoHint: locnoHint ?? null });
    } finally {
      setSummarizingId(null);
    }
  };
  // The chatty back-and-forth → "Create dispatch from recent conversation".
  const summarizeConversation = () =>
    runIntake(buildTranscript(burst), `AI summary of ${burstScopeLabel(burst)}`, "burst", burst.find((m) => m.locno)?.locno ?? null);

  // Reversible per-message cleanup (Seth 2026-06-26) — soft-delete + an 8s Undo.
  const delMsg = useSetMessageDeleted();
  const [undoMsgId, setUndoMsgId] = useState<string | null>(null);
  const deleteMessage = (id: string) => {
    delMsg.mutate({ id, deleted: true });
    setUndoMsgId(id);
    window.setTimeout(() => setUndoMsgId((cur) => (cur === id ? null : cur)), 8000);
  };
  // A Podium webhook write lands on the thread within ~1s (no manual refresh).
  useRealtimeRefresh(["message"], [["messages", custno]]);

  // Day separators: group consecutive messages by calendar day.
  const grouped = useMemo(() => {
    const out: { day: string; items: MessageRow[] }[] = [];
    for (const m of messages ?? []) {
      const day = dayLabel(m.created_at);
      const last = out[out.length - 1];
      if (last && last.day === day) last.items.push(m);
      else out.push({ day, items: [m] });
    }
    return out;
  }, [messages]);

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-slate-500">
          {scoped
            ? "This property's texts, emails and logged calls — newest first."
            : "Every text, email and logged call with this customer — one thread, newest first."}
          {multiProperty && !!locno && (
            <button
              type="button"
              onClick={() => setShowAllProps((v) => !v)}
              className="ml-2 font-medium text-brand-600 underline-offset-2 transition-colors hover:text-brand-700 hover:underline"
            >
              {scoped
                ? otherCount > 0
                  ? `Show ${otherCount} from other properties`
                  : "Showing this property"
                : "Only this property"}
            </button>
          )}
        </p>
        <span className="flex shrink-0 items-center gap-1.5">
          {canSummarize && (
            <button
              type="button"
              onClick={summarizeConversation}
              disabled={summarizingId === "burst"}
              title="AI reads the recent back-and-forth and fills the dispatch"
              className="inline-flex items-center gap-1 rounded-md border border-brand-200 bg-brand-50 px-2.5 py-1.5 text-xs font-semibold text-brand-700 shadow-e1 transition-colors hover:bg-brand-100 disabled:opacity-60"
            >
              {summarizingId === "burst" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              {summarizingId === "burst" ? "Reading…" : "Create dispatch from recent conversation"}
            </button>
          )}
          {canSend && (commsPref?.no_sms ? (
            <span
              title={`Texts are OFF for this customer — ${commsPref.note || "office preference"}. Flip it on the Reach card above to unlock.`}
              className="inline-flex cursor-not-allowed items-center gap-1 rounded-md border border-hairline bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-400"
            >
              <Lock className="h-3.5 w-3.5" />
              Send text
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setTexting(true)}
              className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Send text
            </button>
          ))}
          {canSend && (commsPref?.no_email ? (
            <span
              title={`Email is OFF for this customer — ${commsPref.note || "office preference"}. Flip it on the Reach card above to unlock.`}
              className="inline-flex cursor-not-allowed items-center gap-1 rounded-md border border-hairline bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-400"
            >
              <Lock className="h-3.5 w-3.5" />
              Send email
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setComposing(true)}
              className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800"
            >
              <Mail className="h-3.5 w-3.5" />
              Send email
            </button>
          ))}
          {canLog && (
            <button
              type="button"
              onClick={() => setLogging(true)}
              className="inline-flex items-center gap-1 rounded-md border border-brand-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-brand-700 shadow-e1 transition-colors hover:bg-brand-50"
            >
              <Plus className="h-3.5 w-3.5" />
              Log a call / message
            </button>
          )}
        </span>
      </div>

      {undoMsgId && (
        <div className="mb-2 flex items-center justify-between gap-3 rounded-lg bg-slate-800 px-3 py-2 text-xs text-white shadow-e2">
          <span>Message deleted.</span>
          <button
            type="button"
            onClick={() => { delMsg.mutate({ id: undoMsgId, deleted: false }); setUndoMsgId(null); }}
            className="inline-flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 font-semibold transition-colors hover:bg-white/20"
          >
            <Undo2 className="h-3.5 w-3.5" /> Undo
          </button>
        </div>
      )}

      {isLoading ? (
        <p className="py-12 text-center text-sm text-slate-400">Loading…</p>
      ) : grouped.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white/60 px-6 py-10 text-center">
          <MessageSquare className="mx-auto h-6 w-6 text-slate-300" />
          <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-slate-600">
            No messages on record yet
            <InfoTip label="About messages">
              Texts and emails thread here automatically once the Podium and Microsoft 365 hookups land. Phone calls can be
              logged today — &ldquo;called about the noisy furnace, wants a quote&rdquo; is worth remembering.
            </InfoTip>
          </p>
        </div>
      ) : (
        // Contained "messages app" pane — a soft background so the thread reads
        // as a conversation surface, not floating bubbles (Seth 2026-06-25).
        <div className="rounded-2xl border border-hairline bg-slate-50/80 p-3 shadow-[inset_0_1px_3px_rgba(15,23,42,0.04)] sm:p-4">
          <div className="space-y-5">
            {grouped.map((g) => (
              <div key={g.day}>
                <div className="mb-2.5 flex items-center justify-center">
                  <span className="rounded-full border border-hairline bg-white px-2.5 py-0.5 text-[11px] font-semibold text-slate-400 shadow-e1">
                    {g.day}
                  </span>
                </div>
                <div className="space-y-2">
                  {g.items.map((m) => (
                    <MessageBubble
                      key={m.id}
                      m={m}
                      onMakeDispatch={
                        m.call_event_id
                          ? () => setPendingDispatch({ callId: m.call_event_id!, locnoHint: m.locno })
                          : m.direction === "inbound" && looksLikeWebForm(m.body)
                            ? () => runIntake(m.body ?? "", "AI-filled from the web request", m.id, m.locno)
                            : undefined
                      }
                      busy={summarizingId === m.id}
                      onDelete={() => deleteMessage(m.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {logging && (
        <LogMessageModal custno={custno} onClose={() => setLogging(false)} />
      )}
      {composing && (
        <ComposeEmailModal custno={custno} onClose={() => setComposing(false)} />
      )}
      {texting && (
        <ComposeTextModal custno={custno} onClose={() => setTexting(false)} />
      )}
      {pendingDispatch && prefill && (() => {
        // Resolve the property: explicit pick > the message's own locno >
        // the only property. Multi-property with no signal = ask first.
        const locs = prefill.locations;
        const hint = pendingDispatch.locnoHint && locs.some((l) => l.locno === pendingDispatch.locnoHint)
          ? pendingDispatch.locnoHint : null;
        const effLocno = dispatchLocno ?? hint ?? (locs.length === 1 ? locs[0].locno : null);
        const chosen = effLocno ? locs.find((l) => l.locno === effLocno) ?? null : null;
        const close = () => { setPendingDispatch(null); setDispatchLocno(null); };
        if (!chosen) {
          return (
            <Modal open onClose={close} title="Which Property?" subtitle="This account has more than one — the dispatch books against the property you pick." size="sm">
              <div className="space-y-1.5">
                {locs.map((l) => (
                  <button
                    key={l.locno}
                    type="button"
                    onClick={() => setDispatchLocno(l.locno)}
                    className="flex w-full items-center gap-3 rounded-lg border border-hairline bg-white px-3 py-2.5 text-left transition-colors hover:border-brand-300 hover:bg-brand-50"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-800">{l.add1 ?? `Property ${l.locno}`}</span>
                      <span className="block truncate text-xs text-slate-400">{[l.city, l.phone1].filter(Boolean).join(" · ")}</span>
                    </span>
                    <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-400">#{l.locno}</span>
                  </button>
                ))}
              </div>
            </Modal>
          );
        }
        return (
          <CreateDispatchModal
            custno={custno}
            locno={chosen.locno}
            customerName={prefill.name}
            address={chosen.add1}
            city={chosen.city}
            phone={chosen.phone1}
            sourceCallId={pendingDispatch.callId ?? null}
            initialDescription={pendingDispatch.initialDescription ?? ""}
            sourceCaption={pendingDispatch.sourceCaption ?? null}
            prefillUrgency={pendingDispatch.prefillUrgency ?? null}
            prefillWindow={pendingDispatch.prefillWindow ?? null}
            prefillTypeName={pendingDispatch.prefillTypeName ?? null}
            onClose={close}
          />
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ComposeTextModal (COMMS-04b, Seth 2026-06-11: "add a button to send a
// text") — the SMS sibling of the email composer. A text is a CONVERSATION,
// so it goes to exactly ONE number: candidates are resolved (named contacts
// first, then ESC on-file numbers), opted-out numbers hard-blocked, and the
// send goes out through the office Podium line (podium-send edge fn), landing
// back on this thread.
// ---------------------------------------------------------------------------

function p10(s: string): string {
  return s.replace(/\D/g, "").slice(-10);
}
function prettyPhone(s: string): string {
  const d = p10(s);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : s;
}

export function ComposeTextModal({
  custno,
  onClose,
  initialBody,
  initialTo,
  onSent,
}: {
  custno: string;
  onClose: () => void;
  /** INBOX-COMPOSE-01: fires after the text actually went out (before onClose).
   *  QUOTE-POLISH-05: carries what went out, so a caller can file it (the
   *  proposal thread records office replies). */
  onSent?: (sent: { body: string; to: string }) => void;
  /** FILES-04: "Text it" on a file prefills the share link. */
  initialBody?: string;
  /** Reply-from-inbox: the number that texted in — preselected even if it's
   *  not an on-file line, so a reply always goes back to the right phone. */
  initialTo?: string;
}) {
  const qc = useQueryClient();
  const contacts = (useContacts(custno, null).data ?? []).filter(
    (c) => c.phone && p10(c.phone).length === 10,
  ) as (ContactRow & { phone: string })[];

  // ESC on-file numbers (customer's labeled lines + location lines).
  const { data: onFile } = useQuery({
    queryKey: ["sms_candidates", custno],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<{ phone: string; label: string }[]> => {
      const [cust, locs] = await Promise.all([
        supabase
          .from("mirror_customer")
          .select("phone1, phone2, phone3, phone4, lbl_phone1, lbl_phone2, lbl_phone3, lbl_phone4")
          .eq("custno", custno)
          .maybeSingle(),
        supabase
          .from("mirror_location")
          .select("phone1, phone2, phone3")
          .eq("custno", custno),
      ]);
      const out: { phone: string; label: string }[] = [];
      const seen = new Set<string>();
      const push = (phone: string | null, label: string) => {
        const d = p10(phone ?? "");
        if (d.length !== 10 || seen.has(d)) return;
        seen.add(d);
        out.push({ phone: d, label });
      };
      const c = (cust.data ?? {}) as Record<string, string | null>;
      push(c.phone1, c.lbl_phone1 || "Phone 1");
      push(c.phone2, c.lbl_phone2 || "Phone 2");
      push(c.phone3, c.lbl_phone3 || "Phone 3");
      push(c.phone4, c.lbl_phone4 || "Phone 4");
      for (const l of (locs.data ?? []) as Record<string, string | null>[]) {
        push(l.phone1, "Location line");
        push(l.phone2, "Location line");
        push(l.phone3, "Location line");
      }
      return out;
    },
  });

  // Consent: STOPed numbers are hard-blocked.
  const allPhones = useMemo(() => {
    const s = new Set<string>(contacts.map((c) => p10(c.phone)));
    for (const f of onFile ?? []) s.add(f.phone);
    return [...s];
  }, [contacts, onFile]);
  // CONSENT-01: one model answers "may we text this number for a job update" —
  // a texted STOP, an office do-not-text, a whole-account rule all land here.
  const consent = useCustomerConsent(custno, allPhones.map((ph) => ({ channel: "sms" as const, endpoint: ph })));
  const optedOut = useMemo(
    () => new Set(allPhones.filter((ph) => consent.blocked("sms", ph, "job_updates"))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allPhones, consent.rows],
  );
  const blockReason = (ph: string) => consentReason(consent.rowFor("sms", ph, "job_updates")) ?? "unsubscribed — Pegasus won't send here";

  // Candidate list: named contacts first, then on-file numbers they don't cover.
  const contactPhones = new Set(contacts.map((c) => p10(c.phone)));
  const candidates: { phone: string; label: string; why: string }[] = [
    ...contacts.map((c) => ({
      phone: p10(c.phone),
      label: c.name + (c.role ? ` · ${c.role}` : ""),
      why: c.gets_scheduling ? "gets job updates" : "named contact",
    })),
    ...(onFile ?? [])
      .filter((f) => !contactPhones.has(f.phone))
      .map((f) => ({ phone: f.phone, label: `${f.label} (on file)`, why: "from ESC — no preferences recorded" })),
  ];

  // Reply-from-inbox: make the number that texted in a candidate (even if it's
  // not an on-file line) and preselect it.
  const replyTo = initialTo ? p10(initialTo) : "";
  if (replyTo.length === 10 && !candidates.some((c) => c.phone === replyTo)) {
    candidates.unshift({ phone: replyTo, label: "This Conversation", why: "the number that texted in" });
  }
  const sendable = candidates.filter((c) => !optedOut.has(c.phone));
  const [to, setTo] = useState<string | null>(null);
  const picked =
    to ??
    (replyTo.length === 10 && !optedOut.has(replyTo)
      ? replyTo
      : sendable.length === 1
        ? sendable[0].phone
        : null);
  const [body, setBody] = useState(initialBody ?? "");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [gated, setGated] = useState<string | null>(null);

  const send = async () => {
    if (!picked || !body.trim()) return;
    setSending(true);
    setErr(null);
    setGated(null);
    try {
      const { data, error } = await supabase.functions.invoke("podium-send", {
        body: { to: picked, body: body.trim(), custno },
      });
      const detail = (data as { error?: string; blocked?: string } | null) ?? null;
      // Comms gate (TESTING): the send was correctly held — show it calmly, not as a failure.
      if (detail?.blocked === "testing" || /testing|paused/i.test(detail?.error ?? "")) {
        setGated("Comms are in testing — at go-live this sends for real. Nothing went to the customer.");
        return;
      }
      if (error || detail?.error) {
        setErr(error?.message ?? detail?.error ?? "unknown error");
        return;
      }
      qc.invalidateQueries({ queryKey: ["messages"] });
      onSent?.({ body: body.trim(), to: picked });
      onClose();
    } catch (e) {
      setErr(String(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Send a Text"
      size="sm"
      footer={
        <div className="flex items-center justify-between gap-2">
          <InfoTip label="Where the text goes">Sends from the office Podium line — the reply lands on this thread.</InfoTip>
          <span className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={send}
              disabled={!picked || !body.trim() || sending}
              className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
            >
              {sending
                ? "Sending…"
                : picked
                  ? `Send to ${prettyPhone(picked)}`
                  : "Pick a number"}
            </button>
          </span>
        </div>
      }
    >
      <div className="space-y-3">
        <div>
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Who gets the text — one number, it&apos;s a conversation
          </span>
          {candidates.length === 0 ? (
            <p className="rounded-md border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-400">
              No phone numbers on this account — add a contact with a number
              first (Overview → Contacts).
            </p>
          ) : (
            <div className="space-y-0.5">
              {candidates.map((c) => {
                const blocked = optedOut.has(c.phone);
                return (
                  <label
                    key={c.phone}
                    className={cn(
                      "flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 transition-colors",
                      blocked ? "cursor-not-allowed opacity-60" : "hover:bg-slate-50",
                    )}
                  >
                    <input
                      type="radio"
                      name="sms-to"
                      checked={picked === c.phone}
                      disabled={blocked}
                      onChange={() => setTo(c.phone)}
                      className="mt-0.5 h-3.5 w-3.5 accent-brand-700"
                    />
                    <span className="min-w-0 text-xs">
                      <span className="font-medium text-slate-800">{c.label}</span>
                      <span className="block text-slate-500">{prettyPhone(c.phone)}</span>
                      <span
                        className={cn(
                          "block text-[11px]",
                          blocked ? "font-semibold text-red-600" : "text-slate-400",
                        )}
                      >
                        {blocked ? blockReason(c.phone) : c.why}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="mb-1 flex justify-end">
            <TemplatePicker custno={custno} onPick={(b) => setBody(b)} />
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            autoFocus
            placeholder="Write the text… e.g. Hi, this is Sedgwick Heating — your part arrived. Would you like to schedule the return visit?"
            className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
          />
          <p className="mt-0.5 text-right text-[11px] text-slate-400">
            {body.length} chars{body.length > 160 ? ` · ~${Math.ceil(body.length / 153)} texts` : ""}
          </p>
        </div>
        {gated && (
          <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
            {gated}
          </p>
        )}
        {err && (
          <p className="rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-700">
            Send failed: {err}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// ComposeEmailModal (COMMS-04) — the first real SEND. The core promise:
// the dispatcher never hand-picks addresses blind. Recipients are RESOLVED —
// pick what KIND of message this is, and the contacts whose CONTACT-03 flags
// match are pre-checked, each with its WHY shown. Unsubscribed endpoints
// (comm_endpoint) are hard-blocked, not just unchecked. ESC's on-file emails
// (no preferences recorded) are listed but never auto-checked.
// Review-before-commit: the checked list IS the preview; the button says
// "Send to N".
// ---------------------------------------------------------------------------

const TYPE_META = [
  {
    key: "scheduling",
    label: "Job Update",
    flag: "gets_scheduling",
    hint: "confirmations, reschedules, on-the-way",
  },
  {
    key: "billing",
    label: "Invoice / Receipt",
    flag: "gets_billing",
    hint: "billing only — AP office, not the tenant",
  },
  {
    key: "marketing",
    label: "Marketing",
    flag: "gets_marketing",
    hint: "tune-up reminders, offers",
  },
] as const;
type MsgType = (typeof TYPE_META)[number]["key"];

export function ComposeEmailModal({
  custno,
  onClose,
  initialSubject,
  initialBody,
  initialTo,
  onSent,
}: {
  custno: string;
  onClose: () => void;
  /** INBOX-COMPOSE-01: fires after the email actually went out (before onClose).
   *  QUOTE-POLISH-05: carries what went out (body + the addresses). */
  onSent?: (sent: { body: string; to: string }) => void;
  /** FILES-04: "Email it" on a file prefills the share link. */
  initialSubject?: string;
  initialBody?: string;
  /** Clicking a specific email on the 360 composes TO that address (sent
   *  from Pegasus, never bounced out to Outlook — Seth 2026-06-12). */
  initialTo?: string;
}) {
  const qc = useQueryClient();
  // All contacts on the account (every property + whole-customer).
  const contacts = (useContacts(custno, null).data ?? []).filter(
    (c) => c.email,
  ) as (ContactRow & { email: string })[];

  // ESC on-file location emails — candidates with NO recorded preferences.
  const { data: onFile } = useQuery({
    queryKey: ["loc_emails", custno],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("mirror_location")
        .select("email, email2")
        .eq("custno", custno);
      if (error) throw error;
      const set = new Set<string>();
      for (const r of (data ?? []) as { email: string | null; email2: string | null }[]) {
        for (const e of [r.email, r.email2]) {
          const v = (e ?? "").trim().toLowerCase();
          if (v && v.includes("@")) set.add(v);
        }
      }
      return [...set];
    },
  });

  // Consent: unsubscribed email endpoints are hard-blocked.
  const initTo = (initialTo ?? "").trim().toLowerCase();
  const allEmails = useMemo(() => {
    const s = new Set<string>(contacts.map((c) => c.email.toLowerCase()));
    for (const e of onFile ?? []) s.add(e);
    if (initTo) s.add(initTo);
    return [...s];
  }, [contacts, onFile, initTo]);
  const [type, setType] = useState<MsgType>("scheduling");
  // CONSENT-01: the block depends on WHAT you're sending — an address can be
  // unsubscribed from marketing and still get its invoices.
  const purpose: ConsentPurpose = type === "billing" ? "billing" : type === "marketing" ? "marketing" : "job_updates";
  const consent = useCustomerConsent(custno, allEmails.map((e) => ({ channel: "email" as const, endpoint: e })));
  const optedOut = useMemo(
    () => new Set(allEmails.filter((e) => consent.blocked("email", e, purpose)).map((e) => e.toLowerCase())),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allEmails, consent.rows, purpose],
  );
  const blockReason = (e: string) => consentReason(consent.rowFor("email", e, purpose)) ?? "unsubscribed — Pegasus won't send here";

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [subject, setSubject] = useState(initialSubject ?? "");
  const [body, setBody] = useState(initialBody ?? "");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [gated, setGated] = useState<string | null>(null);

  // Auto-resolve recipients whenever the message type changes: contacts whose
  // flag matches, minus unsubscribed. The dispatcher adjusts from there.
  const flagOf = TYPE_META.find((t) => t.key === type)!.flag;
  useEffect(() => {
    const next = new Set<string>();
    for (const c of contacts) {
      if (
        (c as unknown as Record<string, boolean>)[flagOf] &&
        !optedOut.has(c.email.toLowerCase())
      )
        next.add(c.email.toLowerCase());
    }
    // Dispatcher clicked a specific address on the 360 — keep it checked
    // across type changes (unless unsubscribed).
    if (initTo && !optedOut.has(initTo)) next.add(initTo);
    setChecked(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, contacts.length, optedOut.size, initTo]);

  // Esc / outside-click / focus trap come from the shared Radix Modal.

  const toggle = (email: string) => {
    const v = email.toLowerCase();
    if (optedOut.has(v)) return; // hard block
    const next = new Set(checked);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setChecked(next);
  };

  const contactEmails = new Set(contacts.map((c) => c.email.toLowerCase()));
  const extraOnFile = (onFile ?? []).filter((e) => !contactEmails.has(e));
  // Clicked address that isn't a contact email or an on-file location email
  // still needs a visible row (defensive — shouldn't happen from the 360).
  const extraInitTo =
    initTo && !contactEmails.has(initTo) && !extraOnFile.includes(initTo)
      ? [initTo]
      : [];

  const send = async () => {
    setSending(true);
    setErr(null);
    setGated(null);
    try {
      const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
          to: [...checked],
          subject: subject.trim(),
          text: body,
          custno,
          message_type: type,
        },
      });
      const detail = (data as { error?: string; detail?: unknown; blocked?: string } | null) ?? null;
      // Comms gate (TESTING): held correctly — show calmly, not as a failure.
      if (detail?.blocked === "testing" || /testing|paused/i.test(detail?.error ?? "")) {
        setGated("Comms are in testing — at go-live this sends for real. Nothing went to the customer.");
        return;
      }
      if (error || detail?.error) {
        setErr(
          error?.message ??
            `${detail?.error}${detail?.detail ? ` — ${JSON.stringify(detail.detail).slice(0, 200)}` : ""}`,
        );
        return;
      }
      qc.invalidateQueries({ queryKey: ["messages"] });
      onSent?.({ body, to: [...checked].join(", ") });
      onClose();
    } catch (e) {
      setErr(String(e));
    } finally {
      setSending(false);
    }
  };

  const recipientRow = (
    email: string,
    label: string,
    why: string,
    blocked: boolean,
  ) => (
    <label
      key={email}
      className={cn(
        "flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 transition-colors",
        blocked ? "cursor-not-allowed opacity-60" : "hover:bg-slate-50",
      )}
    >
      <input
        type="checkbox"
        checked={checked.has(email.toLowerCase())}
        disabled={blocked}
        onChange={() => toggle(email)}
        className="mt-0.5 h-3.5 w-3.5 accent-brand-700"
      />
      <span className="min-w-0 text-xs">
        <span className="font-medium text-slate-800">{label}</span>
        <span className="block truncate text-slate-500">{email}</span>
        <span
          className={cn(
            "block text-[11px]",
            blocked ? "font-semibold text-red-600" : "text-slate-400",
          )}
        >
          {blocked ? blockReason(email) : why}
        </span>
      </span>
    </label>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Send Email"
      footer={
        <div className="flex items-center justify-between gap-2">
          <InfoTip label="Where the email goes">Sends as Sedgwick Heating &lt;service@sedgwickheating.com&gt; — it lands on this thread automatically.</InfoTip>
          <span className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={send}
              disabled={
                checked.size === 0 || !subject.trim() || !body.trim() || sending
              }
              className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
            >
              {sending
                ? "Sending…"
                : `Send to ${checked.size || "…"}${checked.size === 1 ? " person" : checked.size > 1 ? " people" : ""}`}
            </button>
          </span>
        </div>
      }
    >
      <div className="space-y-3">
          <div>
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              What kind of message?
            </span>
            <span className="inline-flex items-center overflow-hidden rounded-md border border-hairline bg-white">
              {TYPE_META.map((t, i) => (
                <button
                  key={t.key}
                  type="button"
                  title={t.hint}
                  onClick={() => setType(t.key)}
                  className={cn(
                    "px-2.5 py-1.5 text-xs font-semibold transition-colors",
                    i > 0 && "border-l border-hairline",
                    type === t.key
                      ? "bg-brand-700 text-white"
                      : "text-slate-600 hover:bg-slate-50",
                  )}
                >
                  {t.label}
                </button>
              ))}
            </span>
          </div>

          <div>
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Who gets it — resolved from their contact preferences
            </span>
            {contacts.length === 0 &&
            extraOnFile.length === 0 &&
            extraInitTo.length === 0 ? (
              <p className="rounded-md border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-400">
                No email addresses on this account — add a contact with an
                email first (Overview → Contacts).
              </p>
            ) : (
              <div className="space-y-0.5">
                {contacts.map((c) =>
                  recipientRow(
                    c.email,
                    c.name + (c.role ? ` · ${c.role}` : ""),
                    (c as unknown as Record<string, boolean>)[flagOf]
                      ? `gets ${TYPE_META.find((t) => t.key === type)!.label.toLowerCase()}s`
                      : "not flagged for this — check to send anyway",
                    optedOut.has(c.email.toLowerCase()),
                  ),
                )}
                {extraOnFile.map((e) =>
                  recipientRow(
                    e,
                    "On file (ESC)",
                    "no preferences recorded — check to include",
                    optedOut.has(e),
                  ),
                )}
                {extraInitTo.map((e) =>
                  recipientRow(
                    e,
                    "Clicked address",
                    "the address you clicked on the record",
                    optedOut.has(e),
                  ),
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <TemplatePicker
              custno={custno}
              onPick={(b, subj) => {
                setBody(b);
                if (subj) setSubject(subj);
              }}
            />
          </div>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            placeholder="Write the email…"
            className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
          />
          {gated && (
            <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
              {gated}
            </p>
          )}
          {err && (
            <p className="rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-700">
              Send failed: {err}
            </p>
          )}
      </div>
    </Modal>
  );
}

function MessageBubble({
  m,
  onMakeDispatch,
  busy,
  onDelete,
}: {
  m: MessageRow;
  /** Per-item "Make a dispatch" — a recorded call (attaches the recording) OR a
   *  self-contained web-form request (AI reads it → fills the builder). */
  onMakeDispatch?: () => void;
  /** AI is reading this web-form item right now. */
  busy?: boolean;
  /** Reversible per-message cleanup (Seth 2026-06-26) — soft-delete + Undo. */
  onDelete?: () => void;
}) {
  const meta = CHANNEL_META[m.channel] ?? CHANNEL_META.note;
  const Icon = meta.icon;
  const delBtn = onDelete ? (
    <button
      type="button"
      onClick={onDelete}
      title="Delete this message (you can undo)"
      aria-label="Delete message"
      className="shrink-0 rounded p-1 text-slate-300 opacity-40 transition-all hover:bg-red-50 hover:text-red-500 focus-visible:opacity-100 group-hover:opacity-100"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  ) : null;

  // Internal notes: full-width amber, clearly "never sent to the customer".
  if (m.channel === "note") {
    return (
      <div className="group flex items-start gap-1">
        <div className="flex-1 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2">
          <p className="whitespace-pre-wrap text-sm text-slate-700">{m.body}</p>
          <p className="mt-1 text-[11px] text-amber-700/70">
            <StickyNote className="mr-1 inline h-3 w-3 align-[-2px]" />
            Internal note — never sent · {m.logged_by ?? "—"} ·{" "}
            {timeLabel(m.created_at)}
          </p>
        </div>
        {delBtn}
      </div>
    );
  }

  const outbound = m.direction === "outbound";
  return (
    <div className={cn("group flex items-center gap-1", outbound ? "justify-end" : "justify-start")}>
      {outbound && delBtn}
      <div
        className={cn(
          "max-w-[80%] rounded-xl border px-3 py-2",
          outbound
            ? "border-brand-100 bg-brand-50/70"
            : "border-hairline bg-white",
        )}
      >
        {m.subject && (
          <p className="mb-0.5 text-xs font-semibold text-slate-800">
            {m.subject}
          </p>
        )}
        <p className="whitespace-pre-wrap text-sm text-slate-700">{m.body}</p>
        {/* CALL-REC-1: a recorded call plays inline right on the thread entry. */}
        {m.channel === "call" && m.call_event_id && (
          <CallRecordingPlayer callId={m.call_event_id} className="mt-1.5" />
        )}
        {/* Per-item "Make a dispatch" — a recorded call (recording rides along)
            OR a self-contained web-form request (AI reads it → fills the
            builder). Chatty threads use the header button instead. Seth 2026-06-26. */}
        {onMakeDispatch && (
          <button
            type="button"
            onClick={onMakeDispatch}
            disabled={busy}
            className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-[11px] font-medium text-brand-700 transition-colors hover:bg-brand-50 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarPlus className="h-3.5 w-3.5" />}
            {busy ? "Reading the request…" : "Make a dispatch"}
          </button>
        )}
        {(m.attachments ?? []).length > 0 && (
          <p className="mt-1 flex flex-wrap gap-1">
            {(m.attachments ?? []).map((a) => (
              <span
                key={a.id}
                title={`${a.contentType} · ${Math.round(a.size / 1024)} KB`}
                className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] text-slate-600"
              >
                📎 {a.name}
              </span>
            ))}
          </p>
        )}
        <p className="mt-1 text-[11px] text-slate-400">
          <Icon className="mr-1 inline h-3 w-3 align-[-2px]" />
          {meta.label} {outbound ? "to" : "from"} {m.endpoint || "customer"}
          {" · "}
          {m.status === "logged"
            ? `logged by ${m.logged_by ?? "—"}`
            : ["failed", "undelivered", "bounced", "error"].includes((m.status ?? "").toLowerCase())
              ? null
              : m.status}
          {["failed", "undelivered", "bounced", "error"].includes((m.status ?? "").toLowerCase()) && (
            /* 360-AUDIT #4: a failed text must never read like a sent one. */
            <span className="mx-1 inline-flex items-center gap-1 rounded-full bg-rose-50 px-1.5 py-px font-semibold text-rose-600">
              ⚠ {m.status} — customer did NOT get this
            </span>
          )}
          {" · "}
          {timeLabel(m.created_at)}
        </p>
      </div>
      {!outbound && delBtn}
    </div>
  );
}

/** Log something that already happened (a call, a text sent from the Podium
 * app, an email) — or an internal note. Three close affordances per
 * [[feedback_modal_close_pattern]]. */
function LogMessageModal({
  custno,
  onClose,
}: {
  custno: string;
  onClose: () => void;
}) {
  const log = useLogMessage();
  const [channel, setChannel] = useState<MessageRow["channel"]>("call");
  const [direction, setDirection] = useState<MessageRow["direction"]>("inbound");
  const [subject, setSubject] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [body, setBody] = useState("");
  // Esc / outside-click / focus trap come from the shared Radix Modal.

  const submit = () =>
    log.mutate(
      {
        custno,
        channel,
        direction: channel === "note" ? "inbound" : direction,
        subject: channel === "email" ? subject.trim() || null : null,
        endpoint: endpoint.trim() || null,
        body: body.trim(),
      },
      // 360-AUDIT wave 2 (E): a failed log left the modal open with no word.
      { onSuccess: onClose, onError: onErrToast("Couldn't log it") },
    );

  return (
    <Modal
      open
      onClose={onClose}
      title="Log a call / message"
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!body.trim() || log.isPending}
            className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
          >
            {log.isPending ? "Saving…" : "Log it"}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center overflow-hidden rounded-md border border-hairline bg-white">
              {(["call", "sms", "email", "note"] as const).map((c, i) => {
                const Icon = CHANNEL_META[c].icon;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setChannel(c)}
                    className={cn(
                      "inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold transition-colors",
                      i > 0 && "border-l border-hairline",
                      channel === c
                        ? "bg-brand-700 text-white"
                        : "text-slate-600 hover:bg-slate-50",
                    )}
                  >
                    <Icon className="h-3 w-3" />
                    {CHANNEL_META[c].label}
                  </button>
                );
              })}
            </span>
            {channel !== "note" && (
              <span className="inline-flex items-center overflow-hidden rounded-md border border-hairline bg-white">
                {(
                  [
                    ["inbound", "They reached us"],
                    ["outbound", "We reached them"],
                  ] as const
                ).map(([d, label], i) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDirection(d)}
                    className={cn(
                      "px-2.5 py-1.5 text-xs font-medium transition-colors",
                      i > 0 && "border-l border-hairline",
                      direction === d
                        ? "bg-slate-700 text-white"
                        : "text-slate-600 hover:bg-slate-50",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </span>
            )}
          </div>

          {channel === "email" && (
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject (optional)"
              className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
            />
          )}
          {channel !== "note" && (
            <input
              type="text"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder={
                channel === "email"
                  ? "Their email (optional)"
                  : "Their number (optional)"
              }
              className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
            />
          )}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            autoFocus
            rows={4}
            placeholder={
              channel === "call"
                ? "What was the call about? e.g. called about the noisy furnace — wants a quote on a new blower motor"
                : channel === "note"
                  ? "Internal note — the customer never sees this"
                  : "What was said?"
            }
            className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
          />
      </div>
    </Modal>
  );
}
